// Minimal fetch wrapper. The cookie-bearing endpoints rely on the
// browser sending nc_access automatically.
//
// It also owns session renewal, because a "stay signed in" session outlives
// its 24-hour access cookie (task remember_me, ADR 0190) and an app that is
// already mounted never re-runs its startup probe. Renewing only there would
// leave a dashboard open past the boundary — a boat kiosk, a pinned tab —
// 401ing on every read and every save against a session with months left on
// it. So a 401 here means "try renewing" before it means "signed out".

import { signalDemoFailure } from './lib/demo';
import { isRenewablePath, renewSession, sessionRenewalEnabled } from './lib/sessionRenewal';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * A body sent as it is under its own content type, in place of `body` as
   * JSON: a gzipped run file (TOM-305).
   */
  raw?: { contentType: string; data: Blob };
  idempotencyKey?: string;
  /** Extra request headers (e.g. the FTUE-v2 `Authorization: Reveal <token>`). */
  headers?: Record<string, string>;
  /** Abandons the request: a dropped answer's read-back on Stop or its timeout (TOM-712). */
  signal?: AbortSignal;
}

/**
 * A design page's own answers (TOM-673): the panel gallery draws panels for a
 * sample boat and an empty one that exist only in the browser, through the
 * same hooks a board uses. It answers their requests itself: a value it
 * returns is the response, an Error it returns is a failed request, and
 * undefined sends the request out as usual.
 */
export type LocalApiAnswers = (path: string, method: string, body: unknown) => unknown;

let localAnswers: LocalApiAnswers | null = null;

/** Set, or clear with null, the design page's own answers. */
export function setLocalApiAnswers(answers: LocalApiAnswers | null): void {
  localAnswers = answers;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  if (localAnswers !== null) {
    const answer = localAnswers(path, opts.method ?? 'GET', opts.body);
    if (answer instanceof Error) throw answer;
    if (answer !== undefined) return answer as T;
  }
  return send<T>(path, opts, true);
}

async function send<T>(path: string, opts: ApiOptions, mayRenew: boolean): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  // Only declare a JSON content-type when there's actually a body —
  // Fastify rejects a request that promises JSON but ships an empty
  // body with `fst_err_ctp_empty_json_body` (400). Bodyless mutations
  // like DELETE /v1/boats/:id need no content-type.
  let payload: Blob | string | undefined;
  if (opts.raw !== undefined) {
    headers['content-type'] = opts.raw.contentType;
    payload = opts.raw.data;
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(opts.body);
  }
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: payload,
    credentials: 'same-origin',
    ...(opts.signal === undefined ? {} : { signal: opts.signal }),
  });
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && mayRenew && sessionRenewalEnabled() && isRenewablePath(path)) {
      // Renew, then replay this request exactly once.
      //
      // Replaying a mutation is safe here and only here: a 401 is produced by
      // the principal gate BEFORE any handler runs, so the first attempt
      // changed nothing. The replay also carries the caller's original
      // Idempotency-Key, so even a mutation the server did somehow see
      // collapses onto the stored response rather than happening twice.
      //
      // We replay whatever the renewal returned. A failed renewal is not
      // proof of a dead session: rotation is single-use, so when a browser
      // restores several tabs at once only one wins, and the losers are
      // reading the winner's fresh cookies out of the shared jar. Costs one
      // extra request on a session that really has ended.
      await renewSession();
      return send<T>(path, opts, false);
    }
    // A Try it guest's refused change, or its ended lease (TOM-868).
    signalDemoFailure(path, opts.method ?? 'GET', res.status, body);
    throw Object.assign(new Error(`api ${path} ${res.status}`), {
      status: res.status,
      body,
    });
  }
  return body as T;
}
